import { useEffect } from 'react'
import { useNotifications, useMessages } from '@swim/ui'

const API_URL = (import.meta as ImportMeta & { env: Record<string, string> }).env.VITE_API_URL ?? ''

interface BadgeNavigator extends Navigator {
  setAppBadge: (count?: number) => Promise<void>
  clearAppBadge: () => Promise<void>
}

/**
 * Reflects unread notifications + message threads as a badge on the
 * installed PWA's home-screen icon. No-ops on browsers without the
 * Badging API (Firefox, most Safari contexts). Mounted once at the root
 * of AppRouter so it tracks unread state regardless of route.
 *
 * Polling is force-disabled below (Web-z8o8, 2026-07-11) — Messages/Notifications
 * isn't an actively-developed feature yet, and the always-on poll was the root
 * cause of an overnight incident: a DNS outage made fetchThreads retry every 10s
 * with no backoff for hours, spamming the vite proxy log and eventually freezing
 * the tab. The missing-backoff bug itself is fixed separately in
 * packages/@swim/ui/src/messages/useMessages.ts. To resume this feature, restore
 * `enabled: !!user` on both calls below (re-add `const { user } = useSession()`
 * and the `useSession` import, both removed alongside this change).
 */
export function usePwaBadge(): void {
  const { unreadCount: unreadNotifCount } = useNotifications({
    apiBase: API_URL,
    getAuthHeader: () => null, // Storefront uses cookie auth
    enabled: false, // was: enabled: !!user — see Web-z8o8
    sseEnabled: false, // SSE not built yet (Web-1kr2)
  })

  const { unreadThreadCount } = useMessages({
    apiBase: API_URL,
    getAuthHeader: () => null, // Storefront uses cookie auth
    enabled: false, // was: enabled: !!user — see Web-z8o8
  })

  useEffect(() => {
    if (!('setAppBadge' in navigator)) return
    const badgeNav = navigator as BadgeNavigator
    const total = unreadNotifCount + unreadThreadCount
    const action = total > 0 ? badgeNav.setAppBadge(total) : badgeNav.clearAppBadge()
    // non-blocking: badge API unsupported/denied, purely cosmetic
    action.catch(() => {})
  }, [unreadNotifCount, unreadThreadCount])
}
